import React, { useEffect, useState } from 'react';

import {
  Grid, Stack, Typography,
} from '@mui/material';
import _ from 'lodash';

import Button from '../Button';
import EditField, { FieldType, TypeOption } from '../EditField';

import { FieldName } from './index.styles';

type Data = {
  [key: string]: any
}

type Metadata = {
  [key: string]: {
    label: string,
    editable: boolean,
    hide?: boolean,
    type: FieldType,
    typeOptions?: TypeOption[],
  }
}

type CommitFormFields = {
  [key: string]: {
    xs?: number,
    sm?: number,
    md?: number,
    lg?: number,
    xl?: number
  }
}

type Values = {
  [key: string]: any
}

export interface CommitFormProps {
  /**
   * Default/updated values for the form
   */
  data?: Data,
  /**
   * Metadata
   */
  metadata?: Metadata,
  /**
   * Fields
   */
  fields: CommitFormFields
  /**
   * Whether the form is disabled
   */
  disabled?: boolean
  /**
   * On submission of the form
   */
  onSubmit?: (values: Values) => void,
  /**
   * On update of a single field
   */
  onChange?: (values: Values) => void,
  /**
   * On discard of the form changes
   */
  onDiscard?: () => void,
  /**
   * Loading state for the submission button
   */
  isSubmitLoading?: boolean,
  /**
   * Submission button text
   */
  submitButtonLabel?: string,
  /**
   * Automatically submit the form on each edit
   */
  autoSubmit?: boolean,
}

const CommitForm = ({
  data = {},
  metadata = {},
  fields = {},
  disabled = false,
  onSubmit = () => {},
  onChange = () => {},
  onDiscard = () => {},
  isSubmitLoading = false,
  submitButtonLabel = 'Save',
  autoSubmit = false,
}: CommitFormProps) => {
  const [stagedChanges, setStagedChanges] = useState({});

  const submitStagedChanges = async () => {
    await onSubmit(stagedChanges);
    setStagedChanges({});
  };

  useEffect(() => {
    if (autoSubmit && Object.keys(stagedChanges).length > 0) {
      // Only submit new data if it changes
      const submit = async () => {
        await submitStagedChanges();
      };

      submit();
    }
  }, [stagedChanges]);

  return (
    <Grid container spacing={1}>
      {Object.entries(fields).map(([key, value]) => (
        <Grid item {...value} key={key} alignItems="stretch">
          <Stack direction="row" alignItems="center">
            <FieldName>
              <Typography variant="subtitle2">
                {metadata?.[key]?.label ?? _.startCase(key)}
              </Typography>
            </FieldName>
            <EditField
              value={data?.[key] as string ?? ''}
              type={metadata?.[key]?.type}
              typeOptions={metadata?.[key]?.typeOptions}
              disabled={disabled || !metadata?.[key]?.editable}
              onSubmit={(v) => {
                onChange({
                  [key]: v,
                });
                // Only stage changes if the value is different
                if (v !== data?.[key]) {
                  setStagedChanges({
                    ...stagedChanges,
                    [key]: v,
                  });
                }
              }}
              // Only revert data after submission goes fully through
              revertToOriginalValue={!isSubmitLoading && Object.keys(stagedChanges).length === 0}
            />
          </Stack>
        </Grid>
      ))}
      {(!autoSubmit && !disabled) && (
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <Stack direction="row" spacing={1} justifyContent="right">
            {Object.keys(stagedChanges).length > 0 && (
              <Button
                variant="text"
                color="error"
                size="medium"
                onClick={() => {
                  setStagedChanges({});
                  onDiscard();
                }}
              >
                Discard
              </Button>
            )}
            {Object.keys(fields).some((key) => metadata?.[key]?.editable) && (
              <Button
                variant="contained"
                color="success"
                size="medium"
                loading={isSubmitLoading}
                disabled={Object.keys(stagedChanges).length === 0}
                onClick={submitStagedChanges}
              >
                {submitButtonLabel}
              </Button>
            )}
          </Stack>
        </Grid>
      )}
    </Grid>
  );
};

export default CommitForm;
